import { useEffect, useState } from "react";
import type { MeetingDTO, MeetingMessageDTO, MeetingTranscriptEntry, MeetingViewerDTO } from "../../shared/types";
import { useUser } from "../App";
import { Icon } from "../components/Icon";
import { List, ListRow, listRowCls } from "../components/List";
import { Markdown } from "../components/Markdown";
import { MeetingEnd } from "../components/MeetingEnd";
import { MeetingPlayer } from "../components/MeetingPlayer";
import { MoreOnScroll, usePaged } from "../components/MoreOnScroll";
import { Tabs, useTabParam } from "../components/Tabs";
import { TextLink } from "../components/TextLink";
import { Badge, Button, ButtonLink, ErrorText, PageHeader, Panel } from "../components/ui";
import { api, get } from "../lib/api";
import { IN_CALL, isChat, meetingLength, meetingStart, myActionItems, playable, sinceStart, speakerName, transcriptOf } from "../lib/meetings";

/** What the server says to anyone not in a meeting (`services/meeting-members.ts`). */
const MEMBERS_ONLY = "only the people in this meeting can open it";

/** A meeting's own recording pieces and screens open through the meeting, for the people in it (AIT-335). */
export const meetingFileUrl = (meetingId: string, fileId: string) => `/api/meetings/${meetingId}/files/${fileId}?inline=1`;

const PLATFORM: Record<MeetingDTO["platform"], string> = { google_meet: "Google Meet", zoom: "Zoom", teams: "Microsoft Teams", unknown: "Other" };

/** Where a meeting is, said plainly, while it is not simply done. */
const STATE: Record<MeetingDTO["status"], { words: string; tone: "zinc" | "amber" | "red" | "blue" | "emerald" }> = {
  requested: { words: "Pandas is on its way", tone: "blue" },
  joining: { words: "Pandas is joining", tone: "blue" },
  waiting: { words: "Pandas is waiting to be let in", tone: "blue" },
  in_call: { words: "Pandas is in the call", tone: "emerald" },
  processing: { words: "Minutes being written", tone: "amber" },
  done: { words: "Done", tone: "zinc" },
  failed: { words: "Pandas couldn't finish", tone: "red" },
  cancelled: { words: "Cancelled", tone: "zinc" },
};
/** The bot was in as many meetings as it holds: this one waits for a free slot (AIT-366). */
const stateOf = (m: MeetingDTO) => (m.queuedAt && m.status === "requested" ? { words: "Pandas is waiting for a free slot", tone: "amber" as const } : STATE[m.status]);

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Under a meeting's title: when it was, how long, and where. */
const meetingLine = (m: MeetingDTO) => [when(m.joinedAt ?? m.createdAt), meetingLength(m.joinedAt, m.endedAt), PLATFORM[m.platform]].filter(Boolean).join(" · ");

/**
 * The meetings this person was in that Pandas joined (AIT-335): newest first, 50 at a time. A meeting is private to the
 * people in it, like an email: nobody else's are here, whatever their role.
 */
export function MeetingsPage() {
  const { rows, done, loading, error, more } = usePaged<MeetingDTO>(
    (before) => get<{ meetings: MeetingDTO[] }>(`/api/meetings?${new URLSearchParams({ mine: "1", ...(before ? { before } : {}) })}`).then((r) => r.meetings),
    [],
  );
  return (
    <div className="scroll-thin h-full overflow-x-hidden overflow-y-auto">
      <PageHeader
        title="Meetings"
        subtitle="The meetings you were in that Pandas joined: their minutes, transcript and recording. A meeting is private to the people in it, like an email."
      />
      <div className="space-y-4 px-4 pb-10 md:px-6">
        <List empty={rows === null ? "Loading…" : "No meetings yet. When Pandas joins a meeting you're in, it lands here."}>
          {(rows ?? []).map((m) => (
            <ListRow
              key={m.id}
              to={`/meetings/${m.id}`}
              leading={<Icon name="meetings" className="size-4 text-faint" />}
              title={<span className="min-w-0 truncate font-medium">{m.title || "Untitled meeting"}</span>}
              subtitle={meetingLine(m)}
              trailing={m.status === "done" ? (m.minutes ? <Badge tone="emerald">Minutes</Badge> : <span className="text-faint">No minutes</span>) : <Badge tone={stateOf(m).tone}>{stateOf(m).words}</Badge>}
            />
          ))}
        </List>
        <ErrorText error={error} />
        <MoreOnScroll onMore={more} done={done} loading={loading} />
      </div>
    </div>
  );
}

/**
 * One meeting, for the people in it (AIT-335; the layout is Dimi's, 2026-11-04): who was there under its title, then two
 * tabs — the minutes with this person's own action items above them, and the transcript with the recording on top and
 * the screenshots where they were taken. Its public link is a member's to give and to take back.
 */
export function MeetingPage({ meetingId }: { meetingId: string }) {
  const [meeting, setMeeting] = useState<MeetingDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useTabParam(["minutes", "transcript"] as const);
  const load = () => get<MeetingDTO>(`/api/meetings/${meetingId}`).then((m) => (setMeeting(m), setError(null)), (e) => setError((e as Error).message));

  useEffect(() => {
    setMeeting(null);
    void load();
  }, [meetingId]);
  // While Pandas is in the call or the minutes are being written, and while it is public (its viewers), it is read again.
  const live = meeting && (IN_CALL.includes(meeting.status) || meeting.status === "processing" || meeting.publicToken);
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => void load(), 5000);
    return () => clearInterval(timer);
  }, [live, meetingId]);

  if (!meeting) {
    return (
      <div className="scroll-thin h-full overflow-y-auto">
        <PageHeader title="Meeting" actions={<ButtonLink to="/meetings" size="sm">All meetings</ButtonLink>} />
        <div className="px-4 md:px-6">
          {error === MEMBERS_ONLY ? (
            <p className="text-sm text-muted">Only the people in this meeting can open it.</p>
          ) : error ? (
            <ErrorText error={error} />
          ) : (
            <div className="h-24 animate-pulse rounded-xl bg-fill" />
          )}
        </div>
      </div>
    );
  }
  const transcript = transcriptOf(meeting);
  const members = meeting.people?.members ?? [];
  const guests = meeting.people?.guests ?? [];
  return (
    <div className="scroll-thin h-full overflow-x-hidden overflow-y-auto">
      <PageHeader
        title={meeting.title || "Untitled meeting"}
        subtitle={
          <>
            <span className="block">{meetingLine(meeting)}</span>
            <span className="mt-1 block min-w-0 break-words">
              <b className="font-medium text-fg">In it:</b> {members.map((p) => p.name).join(", ") || "nobody known"}
              {guests.length > 0 && (
                <>
                  {" · "}
                  <b className="font-medium text-fg">Guests:</b> {guests.join(", ")}
                </>
              )}
            </span>
          </>
        }
        actions={<ButtonLink to="/meetings" size="sm">All meetings</ButtonLink>}
      />
      <div className="space-y-4 px-4 pb-10 md:px-6">
        {meeting.status !== "done" && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={stateOf(meeting).tone}>{stateOf(meeting).words}</Badge>
            {IN_CALL.includes(meeting.status) && meeting.captionsStoppedAt && (
              <span className="text-warning">
                Live captions stopped at {new Date(meeting.captionsStoppedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}
              </span>
            )}
          </div>
        )}
        {meeting.error && <p className={`text-sm ${meeting.status === "failed" ? "text-danger" : "text-muted"}`}>{meeting.error}</p>}
        <MeetingEnd meeting={meeting} fileHref={(fileId) => meetingFileUrl(meeting.id, fileId)} />
        <PublishPanel meeting={meeting} onChange={(next) => setMeeting((cur) => ({ ...next, people: cur?.people, memberCount: cur?.memberCount }))} />
        <Tabs
          label="Meeting"
          value={tab}
          onChange={setTab}
          tabs={[
            { key: "minutes", label: "Minutes" },
            { key: "transcript", label: "Transcript", count: transcript.length },
          ]}
        />
        {tab === "minutes" ? <Minutes meeting={meeting} /> : <TranscriptTab meeting={meeting} transcript={transcript} />}
      </div>
    </div>
  );
}

function Minutes({ meeting }: { meeting: MeetingDTO }) {
  const user = useUser();
  if (!meeting.minutes) {
    const why = meeting.status === "processing" ? "The minutes are being written." : IN_CALL.includes(meeting.status) ? "The minutes are written when the meeting ends." : "This meeting has no minutes.";
    return <Panel title="Minutes"><p className="text-sm text-muted">{why}</p></Panel>;
  }
  const mine = myActionItems(meeting.minutes, user.name);
  return (
    <div className="space-y-6">
      {user.role === "admin" && <SendToMe meetingId={meeting.id} />}
      <Panel title="Your action items" hint="The rows of the minutes' action items whose owner is you.">
        {mine ? <Markdown>{mine}</Markdown> : <p className="text-sm text-muted">None for you in these minutes.</p>}
      </Panel>
      <Panel title="Minutes">
        <Markdown>{meeting.minutes}</Markdown>
      </Panel>
    </div>
  );
}

/**
 * A send to me only (AIT-336): Pandito's message for this meeting — its page and the caller's own action items — sent to
 * an admin in it alone, whatever the sending setting says, so they see it before anyone else gets one.
 */
function SendToMe({ meetingId }: { meetingId: string }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    setSent(null);
    try {
      const m = await api<MeetingMessageDTO>(`/api/meetings/${meetingId}/send-to-me`, { method: "POST" });
      if (m.status !== "sent") throw new Error(m.detail ?? "it could not be sent");
      setSent(m.via === "slack_dm" ? "Sent to your Slack DM." : "Sent to your chat with Pandito.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card flex flex-wrap items-center gap-3 p-3">
      <span className="min-w-0 flex-1 text-xs text-muted">
        When sending minutes is on, Pandito tells each Pandas person in this meeting where the minutes are and what their own action items are. See yours first: it goes to you alone.
      </span>
      <Button size="sm" disabled={busy} onClick={send}>Send me mine</Button>
      {sent && <span className="text-xs text-success">{sent}</span>}
      <ErrorText error={error} />
    </div>
  );
}

/** Who said what, a line each, with the screenshots of what was shared where they were taken, and the recording on top. */
function TranscriptTab({ meeting, transcript }: { meeting: MeetingDTO; transcript: MeetingTranscriptEntry[] }) {
  const start = meetingStart(meeting, transcript);
  const items: ({ kind: "line"; at: number; entry: MeetingTranscriptEntry } | { kind: "shot"; at: number; fileId: string })[] = [
    ...transcript.map((entry) => ({ kind: "line" as const, at: Date.parse(entry.at), entry })),
    ...meeting.frames.map((f) => ({ kind: "shot" as const, at: Date.parse(f.at), fileId: f.fileId })),
  ].sort((a, b) => a.at - b.at);
  return (
    <div className="space-y-4">
      {playable(meeting).length > 0 && (
        <MeetingPlayer
          compact
          chunks={playable(meeting)}
          frames={meeting.frames}
          transcript={transcript}
          live={IN_CALL.includes(meeting.status)}
          startAt={new Date(start).toISOString()}
          fileHref={(fileId) => meetingFileUrl(meeting.id, fileId)}
        />
      )}
      <Panel
        title="Transcript"
        hint={
          meeting.finalTranscript.length ? "Transcribed once from the whole recording; each name from who Meet showed speaking and the voices in it."
          : meeting.audio.done > 0 ? "Transcribed from the recording, with the captions naming who spoke." : "From the meeting's live captions."
        }
      >
        {items.length === 0 ? (
          <p className="text-sm text-muted">Nothing was captured.</p>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) =>
              item.kind === "line" ? (
                <div key={i} className="flex gap-3">
                  <span className="w-14 shrink-0 pt-0.5 text-right font-mono text-tiny text-faint tabular-nums">{sinceStart((item.at - start) / 1000)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-xs ${item.entry.named === "unclear" ? "text-muted italic" : "font-semibold"}`}>{speakerName(item.entry.speaker)}</span>
                      {isChat(item.entry.speaker) && <Badge>chat</Badge>}
                    </div>
                    <p className="text-sm break-words whitespace-pre-wrap">{item.entry.text}</p>
                  </div>
                </div>
              ) : (
                <div key={i} className="flex gap-3">
                  <span className="w-14 shrink-0 pt-0.5 text-right font-mono text-tiny text-faint tabular-nums">{sinceStart((item.at - start) / 1000)}</span>
                  <a href={meetingFileUrl(meeting.id, item.fileId)} target="_blank" rel="noopener" className="block min-w-0 flex-1">
                    <img src={meetingFileUrl(meeting.id, item.fileId)} alt="Shared screen" loading="lazy" className="max-h-80 w-full max-w-xl rounded-lg bg-black object-contain ring-1 ring-line" />
                  </a>
                </div>
              ),
            )}
          </div>
        )}
      </Panel>
      {meeting.otherFiles.length > 0 && (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <span>Also kept with the meeting:</span>
          {meeting.otherFiles.map((f) => (
            <TextLink key={f.fileId} href={meetingFileUrl(meeting.id, f.fileId)} newTab title={when(f.at)}>{f.name}</TextLink>
          ))}
        </p>
      )}
    </div>
  );
}

/** The meeting's public page at /m/<token>: a member gives the link, and takes it back (AIT-335). */
function PublishPanel({ meeting, onChange }: { meeting: MeetingDTO; onChange: (m: MeetingDTO) => void }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<MeetingViewerDTO[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  useEffect(() => {
    if (!showHistory) return;
    const load = () => get<MeetingViewerDTO[]>(`/api/meetings/${meeting.id}/viewers`).then(setHistory, () => {});
    void load();
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [showHistory, meeting.id]);
  const hasContent = Boolean(meeting.minutes) || meeting.audio.chunks > 0 || meeting.transcriptCount > 0 || meeting.frames.length > 0;
  const canPublish = [...IN_CALL, "processing"].includes(meeting.status) || hasContent;
  const link = meeting.publicToken ? `${location.origin}/m/${meeting.publicToken}` : null;
  const act = async (method: "POST" | "DELETE") => {
    setBusy(true);
    setError(null);
    try {
      onChange(await api<MeetingDTO>(`/api/meetings/${meeting.id}/publish`, { method }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!link) {
    return (
      <div className="card flex flex-wrap items-center gap-3 p-3">
        <span className="min-w-0 flex-1 text-xs text-muted">Share a public page with the minutes, recording, screenshots and transcript. Anyone with the link can open it.</span>
        <Button className="inline-flex items-center gap-1.5" disabled={busy || !canPublish} onClick={() => act("POST")}>
          <Icon name="live" className="size-4" /> Publish a link
        </Button>
        <ErrorText error={error} />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="card space-y-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-danger/10 px-2 py-0.5 text-xs font-medium text-danger">
            <span className="size-1.5 animate-pulse rounded-full bg-danger" /> Public
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            <Icon name="users" className="size-3.5" /> {meeting.publicViewers} {meeting.publicViewers === 1 ? "viewer" : "viewers"} now
          </span>
          <span className="ml-auto flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={async () => {
                await navigator.clipboard.writeText(link).catch(() => {});
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
            <ButtonLink size="sm" href={link}>
              Open
            </ButtonLink>
            <Button variant="danger" size="sm" disabled={busy} onClick={() => act("DELETE")}>
              Unpublish
            </Button>
          </span>
        </div>
        <code className="block truncate rounded-lg bg-fill px-2 py-1 font-mono text-xs">{link}</code>
        <TextLink quiet onClick={() => setShowHistory(!showHistory)} className="text-xs font-medium">
          {showHistory ? "Hide viewer history" : "Viewer history"}
        </TextLink>
        <ErrorText error={error} />
      </div>
      {showHistory && <ViewerHistory viewers={history} />}
    </div>
  );
}

function ViewerHistory({ viewers }: { viewers: MeetingViewerDTO[] | null }) {
  if (!viewers) return <div className="h-16 animate-pulse rounded-xl bg-fill" />;
  if (viewers.length === 0) return <p className="text-xs text-muted">Nobody has opened the page yet.</p>;
  const time = (iso: string) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  return (
    <List title={`${viewers.length} ${viewers.length === 1 ? "visitor" : "visitors"}`} hint="One per browser tab." scroll="short">
      {viewers.map((v) => (
        <div key={v.viewerId} className={`flex items-start gap-2 text-xs ${listRowCls}`}>
          <span className={`mt-1 size-2 shrink-0 rounded-full ${v.watching ? "bg-success" : "bg-faint"}`} title={v.watching ? "Watching now" : "Left"} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className="font-medium">{[v.city, v.country].filter(Boolean).join(", ") || "Unknown location"}</span>
              {v.countryCode && <span className="rounded bg-fill px-1 font-mono text-tiny text-muted">{v.countryCode}</span>}
              <span className="font-mono text-tiny text-muted">{v.ip ?? "no IP"}</span>
            </div>
            <div className="truncate text-muted">{[v.org, v.device].filter(Boolean).join(" · ") || "—"}</div>
          </div>
          <div className="shrink-0 text-right text-tiny text-faint tabular-nums">
            <div>{v.watching ? "watching now" : `last ${time(v.lastSeenAt)}`}</div>
            <div>first {time(v.firstSeenAt)}</div>
          </div>
        </div>
      ))}
    </List>
  );
}
